import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";
import Modal from "@/components/base/Modal";
import InvoicePreview from "@/components/feature/InvoicePreview";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useBranches } from "@/hooks/useBranches";
import { useCatalogData } from "@/hooks/useCatalog";
import { useCustomersData } from "@/hooks/useCustomers";
import { useOffersData } from "@/hooks/useOffers";
import { useServicesData } from "@/hooks/useServices";
import { usePaymentMethods, useShortcuts, usePrinterSettings } from "@/hooks/usePosConfig";
import { useInvoicePreview } from "@/hooks/useInvoicePreview";
import { resolvePrice } from "@/lib/posPricing";
import { createSale, type PaymentPayload, type SaleItemPayload } from "@/lib/salesApi";
import { deleteHeldSale, loadHeldSales, suspendSale } from "@/lib/heldSalesApi";
import { logAudit } from "@/lib/audit";
import { formatMoney } from "@/lib/format";
import { useCart, type CartItem } from "@/pages/pos/useCart";
import ProductBrowser from "@/pages/pos/components/ProductBrowser";
import CartPanel from "@/pages/pos/components/CartPanel";
import CustomerPickerModal from "@/pages/pos/components/CustomerPickerModal";
import CheckoutModal, { type CheckoutPaymentRow } from "@/pages/pos/components/CheckoutModal";
import SuspendSaleModal from "@/pages/pos/components/SuspendSaleModal";
import HeldSalesModal from "@/pages/pos/components/HeldSalesModal";
import type { Customer, HeldSale, Product, WorkshopService } from "@/types";

const LAST_INVOICE_KEY = "rkc_last_invoice";

export default function PosPage() {
  const { profile, roleName, isAdmin, hasPermission, signOut } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const { branches } = useBranches();
  const catalog = useCatalogData();
  const servicesData = useServicesData();
  const offersData = useOffersData();
  const customersData = useCustomersData();
  const { methods } = usePaymentMethods();
  const { shortcuts } = useShortcuts();
  const printer = usePrinterSettings();

  const cart = useCart();

  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"products" | "services">("products");
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [receiptInvoiceId, setReceiptInvoiceId] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<"catalog" | "cart">("catalog");
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(profile?.branch_id ?? null);
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [heldOpen, setHeldOpen] = useState(false);
  const [heldSales, setHeldSales] = useState<HeldSale[]>([]);
  const [heldLoading, setHeldLoading] = useState(false);
  const [suspendProcessing, setSuspendProcessing] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);

  const receipt = useInvoicePreview(receiptInvoiceId);

  const activeBranches = useMemo(() => branches.filter((b) => b.active), [branches]);
  const branchId = useMemo(() => {
    if (selectedBranchId) return selectedBranchId;
    return activeBranches[0]?.id ?? null;
  }, [selectedBranchId, activeBranches]);

  const refreshHeldSales = useCallback(async () => {
    if (!branchId) {
      setHeldSales([]);
      return;
    }
    setHeldLoading(true);
    try {
      setHeldSales(await loadHeldSales(branchId));
    } finally {
      setHeldLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    refreshHeldSales();
  }, [refreshHeldSales]);

  useEffect(() => {
    if (profile?.branch_id) setSelectedBranchId(profile.branch_id);
  }, [profile?.branch_id]);

  const cashierName = `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario";
  const canCharge = isAdmin || hasPermission("sales.create");
  const canDiscount = isAdmin || hasPermission("discounts.apply");
  const canUsePos = isAdmin || hasPermission("pos.view");

  const getStock = useCallback(
    (productId: string): number => {
      const bucket = catalog.stockByProduct[productId];
      if (!bucket) return 0;
      if (branchId) return Number(bucket.byBranch[branchId] ?? 0);
      return bucket.total;
    },
    [catalog.stockByProduct, branchId],
  );

  const addProductToCart = useCallback(
    (product: Product) => {
      const price = resolvePrice(product, customer?.customer_type ?? null, offersData.offers);
      cart.addProduct(product, price, getStock(product.id));
    },
    [cart, customer, offersData.offers, getStock],
  );

  const handleAddService = useCallback(
    (service: WorkshopService) => {
      cart.addService(service);
    },
    [cart],
  );

  const handleSearchSubmit = () => {
    const term = search.trim().toLowerCase();
    if (!term) return;
    const match = catalog.products.find(
      (p) => (p.barcode ?? "").toLowerCase() === term || (p.sku ?? "").toLowerCase() === term,
    );
    if (match) {
      addProductToCart(match);
      setSearch("");
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const handleSuspend = async (label: string) => {
    if (!profile?.id) {
      toast.error("No se pudo identificar al usuario.");
      return;
    }
    setSuspendProcessing(true);
    try {
      const result = await suspendSale({
        branchId,
        userId: profile.id,
        customerId: customer?.id ?? null,
        label,
        cart: cart.items,
      });
      if (!result.ok) {
        toast.error(result.error ?? "No se pudo suspender la venta.");
        return;
      }
      await logAudit({
        userId: profile.id,
        userName: cashierName,
        action: "suspender_venta",
        entity: "held_sales",
        entityId: result.held?.id ?? "",
        branchId,
        newValue: { label, items: cart.totals.itemCount, total: cart.totals.total },
      });
      toast.success("Venta suspendida. Puedes recuperarla cuando quieras.");
      cart.clear();
      setCustomer(null);
      setSuspendOpen(false);
      refreshHeldSales();
    } finally {
      setSuspendProcessing(false);
    }
  };

  const handleResume = async (held: HeldSale) => {
    const items = Array.isArray(held.cart) ? (held.cart as CartItem[]) : [];
    cart.loadItems(items);
    if (held.customer_id) {
      setCustomer(customersData.customers.find((c) => c.id === held.customer_id) ?? null);
    } else {
      setCustomer(null);
    }
    await deleteHeldSale(held.id);
    setHeldOpen(false);
    setMobileView("cart");
    refreshHeldSales();
    toast.success("Venta recuperada en el carrito.");
  };

  const handleDeleteHeld = async (held: HeldSale) => {
    const result = await deleteHeldSale(held.id);
    if (!result.ok) {
      toast.error(result.error ?? "No se pudo eliminar la venta suspendida.");
      return;
    }
    toast.info("Venta suspendida eliminada.");
    refreshHeldSales();
  };

  const handleConfirmCheckout = async (payments: CheckoutPaymentRow[], notes: string) => {
    if (!branchId) {
      toast.error("No hay una sucursal activa asignada.");
      return;
    }
    setProcessing(true);
    try {
      const items: SaleItemPayload[] = cart.items.map((item) => ({
        item_type: item.item_type,
        product_id: item.product_id,
        service_id: item.service_id,
        name: item.name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount: item.discount,
        tax_rate: item.tax_rate,
        cost_price: item.cost_price,
        unit: item.unit,
      }));
      const paymentPayload: PaymentPayload[] = payments.map((p) => ({
        method: p.method,
        amount: p.amount,
        reference: p.reference,
      }));

      const result = await createSale({
        branchId,
        customerId: customer?.id ?? null,
        vehicleId: null,
        notes: notes || null,
        items,
        payments: paymentPayload,
      });

      if (!result.ok) {
        toast.error(result.error ?? "No se pudo completar la venta.");
        return;
      }

      await logAudit({
        userId: profile?.id ?? null,
        userName: cashierName,
        action: "crear",
        entity: "invoices",
        entityId: result.invoiceId ?? "",
        branchId,
        newValue: {
          number: result.number,
          total: result.total,
          customer: customer ? `${customer.first_name} ${customer.last_name}` : "Consumidor final",
          items: cart.totals.itemCount,
        },
      });

      toast.success(`Venta registrada · Factura ${result.number}`);
      localStorage.setItem(LAST_INVOICE_KEY, result.invoiceId ?? "");
      cart.clear();
      setCustomer(null);
      setCheckoutOpen(false);
      setMobileView("catalog");
      if (result.invoiceId) setReceiptInvoiceId(result.invoiceId);
    } finally {
      setProcessing(false);
    }
  };

  const reprintLast = useCallback(() => {
    const lastId = localStorage.getItem(LAST_INVOICE_KEY);
    if (lastId) {
      setReceiptInvoiceId(lastId);
    } else {
      toast.info("Todavía no hay una factura reciente para reimprimir.");
    }
  }, [toast]);

  // Keyboard shortcuts (F1–F12).
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const key = event.key;
      if (!/^F\d{1,2}$/.test(key)) return;
      const num = Number(key.slice(1));
      if (num < 1 || num > 12) return;
      event.preventDefault();
      if (key === "F1") searchRef.current?.focus();
      else if (key === "F2") setCustomerModalOpen(true);
      else if (key === "F3") navigate("/admin/customers");
      else if (key === "F4") {
        // Descuento por línea: se aplica desde el carrito (requiere permiso).
      } else if (key === "F6") {
        const last = cart.items[cart.items.length - 1];
        if (last) cart.removeItem(last.key);
      } else if (key === "F8") {
        if (cart.items.length > 0) setSuspendOpen(true);
      } else if (key === "F9") {
        refreshHeldSales();
        setHeldOpen(true);
      } else if (key === "F10") {
        cart.clear();
        setCustomer(null);
        toast.info("Nueva venta iniciada.");
      } else if (key === "F11") reprintLast();
      else if (key === "F12") {
        if (cart.items.length > 0) setCheckoutOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cart, navigate, reprintLast, toast, refreshHeldSales]);

  if (!canUsePos) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 px-4">
        <div className="max-w-md rounded-lg border border-background-200 bg-background-100 p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-lock-2-line text-2xl"></i>
          </span>
          <h1 className="font-heading mt-4 text-lg font-bold text-foreground-950">Acceso restringido</h1>
          <p className="mt-2 text-sm text-foreground-600">
            Tu usuario no tiene permiso para usar el punto de venta.
          </p>
          <button
            type="button"
            onClick={handleSignOut}
            className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-logout-box-r-line text-base"></i>
            Cerrar sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <header className="sticky top-0 z-30 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex h-16 w-full max-w-[1600px] items-center gap-3 px-4 md:px-6">
          <BrandLogo size={36} />
          <div className="min-w-0">
            <p className="font-heading truncate text-sm font-bold text-foreground-950">Punto de Venta</p>
            <p className="truncate text-[11px] text-foreground-500">
              {cashierName}
              {activeBranches.length > 0 && branchId
                ? ` · ${activeBranches.find((b) => b.id === branchId)?.name ?? ""}`
                : ""}
            </p>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {isAdmin && activeBranches.length > 1 ? (
              <select
                value={branchId ?? ""}
                onChange={(event) => setSelectedBranchId(event.target.value)}
                className="hidden max-w-[180px] appearance-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs font-semibold text-foreground-800 outline-none focus:border-primary-400 sm:block"
              >
                {activeBranches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            ) : null}

            <button
              type="button"
              onClick={() => setShortcutsOpen(true)}
              className="hidden items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-semibold text-foreground-800 transition-colors hover:bg-background-100 sm:flex"
            >
              <i className="ri-keyboard-line text-base"></i>
              Atajos
            </button>

            {roleName === "admin" ? (
              <Link
                to="/admin"
                className="hidden items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs font-semibold text-foreground-800 transition-colors hover:bg-background-100 sm:flex"
              >
                <i className="ri-dashboard-3-line text-base"></i>
                Panel
              </Link>
            ) : null}

            <button
              type="button"
              onClick={handleSignOut}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-primary-50 px-3.5 py-2 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100"
            >
              <i className="ri-logout-box-r-line text-base"></i>
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1600px] flex-1 px-4 py-4 pb-24 md:px-6 lg:pb-6">
        <div className="grid grid-cols-1 gap-4 lg:h-[calc(100vh-7.5rem)] lg:grid-cols-[1fr_400px]">
          <div
            className={`${
              mobileView === "catalog" ? "flex" : "hidden"
            } h-[calc(100vh-12.5rem)] min-h-0 flex-col lg:flex lg:h-full`}
          >
            <ProductBrowser
              products={catalog.products}
              services={servicesData.services}
              categories={catalog.categories}
              offers={offersData.offers}
              customerType={customer?.customer_type ?? null}
              branchId={branchId}
              stockByProduct={catalog.stockByProduct}
              loading={catalog.loading || servicesData.loading}
              error={catalog.error}
              tab={tab}
              onTabChange={setTab}
              search={search}
              onSearchChange={setSearch}
              searchRef={searchRef}
              onAddProduct={addProductToCart}
              onAddService={handleAddService}
              onRetry={catalog.reload}
            />
          </div>

          <div
            className={`${
              mobileView === "cart" ? "flex" : "hidden"
            } h-[calc(100vh-12.5rem)] min-h-0 flex-col lg:flex lg:h-full`}
          >
            <CartPanel
              items={cart.items}
              totals={cart.totals}
              canDiscount={canDiscount}
              customer={customer}
              onOpenCustomer={() => setCustomerModalOpen(true)}
              onIncrement={cart.increment}
              onDecrement={cart.decrement}
              onSetQty={cart.setQuantity}
              onSetDiscount={cart.setDiscount}
              onRemove={cart.removeItem}
              onCheckout={() => {
                if (!canCharge) {
                  toast.error("Tu usuario no tiene permiso para cobrar ventas.");
                  return;
                }
                setCheckoutOpen(true);
              }}
              onClear={cart.clear}
              onBrowse={() => setMobileView("catalog")}
              onSuspend={() => {
                if (cart.items.length === 0) {
                  toast.info("El carrito está vacío.");
                  return;
                }
                setSuspendOpen(true);
              }}
              onOpenHeld={() => {
                refreshHeldSales();
                setHeldOpen(true);
              }}
              heldCount={heldSales.length}
            />
          </div>
        </div>
      </main>

      <div className="fixed inset-x-4 bottom-4 z-30 flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3 shadow-none lg:hidden">
        <div className="min-w-0">
          <p className="text-[11px] text-foreground-500">
            {cart.totals.itemCount} línea(s) · {cart.totals.unitCount} unidad(es)
          </p>
          <p className="font-heading text-lg font-bold text-primary-600">{formatMoney(cart.totals.total)}</p>
        </div>
        {mobileView === "catalog" ? (
          <button
            type="button"
            onClick={() => setMobileView("cart")}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50"
          >
            <i className="ri-shopping-cart-2-line text-base"></i>
            Ver carrito
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setMobileView("catalog")}
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-bold text-background-50"
          >
            <i className="ri-store-2-line text-base"></i>
            Catálogo
          </button>
        )}
      </div>

      <CustomerPickerModal
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        customers={customersData.customers}
        selectedId={customer?.id ?? null}
        onSelect={(selected) => setCustomer(selected)}
      />

      <SuspendSaleModal
        open={suspendOpen}
        onClose={() => setSuspendOpen(false)}
        processing={suspendProcessing}
        itemCount={cart.totals.itemCount}
        total={cart.totals.total}
        onConfirm={handleSuspend}
      />

      <HeldSalesModal
        open={heldOpen}
        onClose={() => setHeldOpen(false)}
        heldSales={heldSales}
        loading={heldLoading}
        onResume={handleResume}
        onDelete={handleDeleteHeld}
      />

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        total={cart.totals.total}
        methods={methods}
        processing={processing}
        onConfirm={handleConfirmCheckout}
      />

      <Modal
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
        title="Atajos de teclado"
        subtitle="Configurables desde Configuración → Atajos POS."
        size="md"
      >
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {shortcuts.map((shortcut) => (
            <li
              key={shortcut.id}
              className="flex items-center gap-3 rounded-md border border-background-200 bg-background-100 px-3 py-2.5"
            >
              <span className="flex h-7 min-w-[44px] items-center justify-center rounded-md bg-primary-100 px-2 text-[11px] font-bold text-primary-700">
                {shortcut.key_code}
              </span>
              <span className="truncate text-sm text-foreground-700">
                {shortcut.description || shortcut.action}
              </span>
            </li>
          ))}
        </ul>
      </Modal>

      <Modal
        open={!!receiptInvoiceId}
        onClose={() => setReceiptInvoiceId(null)}
        title="Venta completada"
        subtitle={receipt.data?.invoice.number ? `Factura ${receipt.data.invoice.number}` : undefined}
        size="lg"
        footer={
          <button
            type="button"
            onClick={() => {
              setReceiptInvoiceId(null);
              setMobileView("catalog");
            }}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-add-line text-base"></i>
            Nueva venta
          </button>
        }
      >
        <InvoicePreview
          data={receipt.data}
          loading={receipt.loading}
          error={receipt.error}
          posWidth={printer.pos_width}
          printer={printer}
        />
      </Modal>
    </div>
  );
}